/**
 * ReviewMessagingPanel
 *
 * Inline SMS/Email preview panel shown alongside the Review step checklist.
 * Uses the shared CaseSetupMessagingContext for state so the FinishButton
 * can access the same draft content when sending on completion.
 */
import { Loader2, Mail, MessageSquare } from 'lucide-react';
import React from 'react';

import { CaseSetupEmailPreview, CaseSetupSmsPreview } from '@/components/case-setup/CaseSetupMessaging';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PREFERRED_LANGUAGE_OPTIONS,
  type PreferredLanguageValue,
} from '@/services/types/matter-client-types';

import { useCaseSetupMessaging } from '../../context/CaseSetupMessagingContext';

interface ReviewMessagingPanelProps {
  matterId: string;
}

export const ReviewMessagingPanel: React.FC<ReviewMessagingPanelProps> = ({
  matterId,
}) => {
  const messaging = useCaseSetupMessaging();

  return (
    <div className="flex h-full flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
      {/* Header */}
      <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 px-3 py-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
            Welcome Contact
          </h3>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
            Preview the intro message sent on completion.
          </p>
        </div>

        <div className="shrink-0">
          <Select
            value={messaging.language}
            onValueChange={(value) => messaging.setLanguage(value as PreferredLanguageValue)}
            disabled={messaging.isLanguageUpdating}
          >
            <SelectTrigger className="h-7 min-w-[110px] px-2 text-xs border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
              <SelectValue>
                <span className="truncate font-medium text-zinc-900 dark:text-white">
                  {messaging.language}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PREFERRED_LANGUAGE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs for SMS / Email */}
      <Tabs defaultValue="sms" className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900">
          <TabsList className="flex w-full rounded-none bg-transparent p-0">
            <TabsTrigger
              value="sms"
              className="flex flex-1 items-center gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-700 data-[state=active]:border-indigo-500 data-[state=active]:bg-transparent data-[state=active]:text-indigo-600 data-[state=active]:shadow-none dark:text-zinc-400 dark:hover:text-zinc-300 dark:data-[state=active]:text-indigo-400"
            >
              {messaging.smsIsLoading ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <MessageSquare className="size-3" />
              )}
              SMS
              {messaging.smsUnavailableReason && (
                <span className="text-zinc-400 dark:text-zinc-500">(unavailable)</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="email"
              className="flex flex-1 items-center gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-700 data-[state=active]:border-indigo-500 data-[state=active]:bg-transparent data-[state=active]:text-indigo-600 data-[state=active]:shadow-none dark:text-zinc-400 dark:hover:text-zinc-300 dark:data-[state=active]:text-indigo-400"
            >
              {messaging.emailIsLoading ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Mail className="size-3" />
              )}
              Email
              {messaging.emailUnavailableReason && (
                <span className="text-zinc-400 dark:text-zinc-500">(unavailable)</span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="sms" forceMount className="min-h-0 flex-1 overflow-hidden mt-0 data-[state=inactive]:hidden">
          <CaseSetupSmsPreview
            matterId={matterId}
            enabled={messaging.smsEnabled}
            onEnabledChange={messaging.setSmsEnabled}
            message={messaging.smsMessage}
            onMessageChange={messaging.setSmsMessage}
            unavailableReason={messaging.smsUnavailableReason}
            onLoadingChange={messaging.setSmsIsLoading}
            languageOverride={messaging.language}
          />
        </TabsContent>
        <TabsContent value="email" forceMount className="min-h-0 flex-1 overflow-hidden mt-0 data-[state=inactive]:hidden">
          <CaseSetupEmailPreview
            matterId={matterId}
            enabled={messaging.emailEnabled}
            onEnabledChange={messaging.setEmailEnabled}
            subject={messaging.emailSubject}
            onSubjectChange={messaging.setEmailSubject}
            body={messaging.emailBody}
            onBodyChange={messaging.setEmailBody}
            unavailableReason={messaging.emailUnavailableReason}
            onLoadingChange={messaging.setEmailIsLoading}
            languageOverride={messaging.language}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
